import { useRef } from "react";
import { Button, Progress, Tooltip, TooltipContent, TooltipTrigger } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Upload, Loader2 } from "lucide-react";
import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { toast } from "sonner";
import type { FileUploadBlock as FileUploadBlockType } from "../../../shared/schema";
import { useEnduserStore } from "../../store/hooks";

interface FileUploadBlockProps {
  block: FileUploadBlockType;
}

export function FileUploadBlock({ block }: FileUploadBlockProps) {
  const { t } = useTranslation("files");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const session = useEnduserStore((state) => state.session);
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);

  const { uploadFileAsync, progress, isLoading, reset } = useUploadTellescopeFile({
    session,
    confirmMutationOptions: {
      onSuccess: () => {
        toast.success(t("File uploaded successfully"));
        reset();
      },
      onError: () => {
        toast.error(t("Failed to upload file"));
        reset();
      },
    },
  });

  const handleClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentEnduser) return;

    // Frontend validation: file size
    const maxSizeMB = block.props.maxFileSizeMB;
    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      toast.error(t("File too large. Maximum size: {{max}}MB", { max: maxSizeMB }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    try {
      await uploadFileAsync({ name: file.name, size: file.size, type: file.type, enduserId: currentEnduser.id }, file);
    } catch {
      // Error handled by onError callback
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const text = block.props.text || t("Upload File");
  const buttonVariant = block.props.buttonVariant ?? "default";

  const Icon = isLoading ? Loader2 : Upload;
  const iconClass = isLoading ? "h-4 w-4 animate-spin" : "h-4 w-4";

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      onChange={handleFileChange}
      disabled={isLoading}
      accept={block.props.acceptedFileTypes}
    />
  );

  // Icon variant
  if (block.props.variant === "icon") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="icon" onClick={handleClick} disabled={isLoading}>
            <Icon className={iconClass} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isLoading ? `${t("Uploading")}... ${progress}%` : text}</p>
        </TooltipContent>
        {fileInput}
      </Tooltip>
    );
  }

  // Button variant (default)
  return (
    <div className="space-y-2">
      <Button variant={buttonVariant} onClick={handleClick} disabled={isLoading} className="gap-2">
        <Icon className={iconClass} />
        {isLoading ? `${t("Uploading")}... ${progress}%` : text}
      </Button>
      {isLoading && <Progress value={progress} className="h-1 w-full" />}
      {fileInput}
    </div>
  );
}
